import { env } from "@/env";
import { CartResponse } from "@/networking/carts.api";
import Cookies from "js-cookie";
import { z } from "zod";

export function createApiResponseZod<T extends z.ZodType>(dataSchema: T) {
  return z.object({
    data: dataSchema,
    status: z.string(),
  });
}
export function getApiHeaders() {
  return {
    "Content-Type": "application/json",
    "x-fluid-client": "checkout",
  };
}

export const apiErrorSchema = z.object({
  message: z.string(),
  status: z.number(),
  data: z.unknown().optional(),
});

export async function fetchApi<
  TInput extends z.ZodType,
  TOutput extends z.ZodType,
>({
  endpoint,
  method = "POST",
  input,
  inputSchema,
  outputSchema,
  fetchOptions = {},
}: {
  endpoint: string;
  method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  input?: z.infer<TInput>;
  inputSchema?: TInput;
  outputSchema: TOutput;
  fetchOptions?: RequestInit;
}): Promise<z.infer<TOutput>> {
  if (input && inputSchema) {
    inputSchema.parse(input);
  }

  let url = endpoint.startsWith("http")
    ? endpoint
    : `${env.NEXT_PUBLIC_FLUID_API_URL}${endpoint}`;

  if (method === "GET" && input) {
    const params = new URLSearchParams();
    Object.entries(input).forEach(([key, value]) => {
      params.append(key, String(value));
    });
    url += `?${params.toString()}`;
  }

  const response = await fetch(url, {
    method,
    headers: getApiHeaders(),
    ...(input && method !== "GET" && { body: JSON.stringify(input) }),
    ...fetchOptions,
  });

  const data = await response.json();

  if (!response.ok) {
    if (response.status === 401 && Cookies.get("jwt")) {
      Cookies.remove("jwt");
      window.location.reload();
    }

    // Handle 302 redirects specially - they're valid responses
    if (response.status === 302) {
      // For redirects, just return the data as-is
      return data as z.infer<TOutput>;
    }

    // For other non-ok responses, throw error
    if (data.redirect_url) {
      // Let it continue to parse as normal response
    } else {
      throw apiErrorSchema.parse({
        message: data.message || `${method} request failed`,
        status: response.status,
        data,
      });
    }
  }

  const result = outputSchema.safeParse(data);
  if (!result.success) {
    console.error(`error parsing api response. ${endpoint}`, result.error);
    return data as z.infer<TOutput>;
  }
  return result.data as z.infer<TOutput>;
}

export async function fetchSpreedlyApi<TOutput extends z.ZodType>({
  endpoint,
  input,
  outputSchema,
}: {
  endpoint: string;
  input?: Record<string, unknown>;
  outputSchema: TOutput;
}): Promise<z.infer<TOutput>> {
  const url = `https://core.spreedly.com/v1/${endpoint}`;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    mode: "cors",
    ...(input && { body: JSON.stringify(input) }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw apiErrorSchema.parse({
      message: data.message || `POST request failed`,
      status: response.status,
      data,
    });
  }

  const result = outputSchema.safeParse(data);
  if (!result.success) {
    console.error("error parsing spreedly api response", result.error);
    return data as z.infer<TOutput>;
  }
  return result.data as z.infer<TOutput>;
}

export async function fetchApiFormData<
  TInput extends z.ZodType,
  TOutput extends z.ZodType,
>({
  endpoint,
  method = "POST",
  input,
  inputSchema,
  outputSchema,
}: {
  endpoint: string;
  method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  input?: z.infer<TInput>;
  inputSchema?: TInput;
  outputSchema: TOutput;
}): Promise<z.infer<TOutput>> {
  if (input && inputSchema) {
    inputSchema.parse(input);
  }

  let url = endpoint.startsWith("http")
    ? endpoint
    : `${env.NEXT_PUBLIC_FLUID_API_URL}${endpoint}`;

  if (method === "GET" && input) {
    const params = new URLSearchParams();
    Object.entries(input).forEach(([key, value]) => {
      params.append(key, String(value));
    });
    url += `?${params.toString()}`;
  }

  const response = await fetch(url, {
    method,
    ...(input && method !== "GET" && { body: input as BodyInit }),
  });

  const data = await response.json();
  if (!response.ok) {
    throw apiErrorSchema.parse({
      message: data.message || `${method} request failed`,
      status: response.status,
      data,
    });
  }

  const result = outputSchema.safeParse(data);
  if (!result.success) {
    // Sentry.captureMessage("error parsing api response", {
    //   extra: {
    //     input,
    //     endpoint,
    //     method,
    //     url,
    //     response: data,
    //     error: result.error,
    //   },
    // });
    console.error(`error parsing api response. ${endpoint}`, result.error);
    return data as z.infer<TOutput>;
  }
  return result.data as z.infer<TOutput>;
}

export type ApiError = {
  message: string;
  status?: number;
  data?: {
    error_message?: string;
    errors?: Record<string, string[]>;
  };
};

export function isApiError(err: unknown): err is ApiError {
  return (
    typeof err === "object" &&
    err !== null &&
    "message" in err &&
    typeof (err as Record<string, unknown>).message === "string" &&
    (!("data" in err) ||
      typeof (err as Record<string, unknown>).data !== "object" ||
      (err as Record<string, unknown>).data === null ||
      "error_message" in ((err as Record<string, unknown>).data as object) ||
      "errors" in ((err as Record<string, unknown>).data as object))
  );
}

export const hasCartItemErrors = (cart: CartResponse["cart"] | null) => {
  if (!cart) return false;
  return cart.items?.some((item) => item.errors && item.errors.length > 0);
};

export const hasCartErrors = (cart: CartResponse["cart"] | null) => {
  if (!cart) return false;

  return cart.messages?.errors && cart.messages?.errors?.length > 0;
};
